import React, { useState } from 'react';
import Matchup from "../../ui/Matchup";
import FloatingButton from '../../ui/FloatingButton';
import GenerateScheduleModal from '../GenerateScheduleModal';
import { apiClient } from '../../../services/api/apiService';
import { scheduleErrorMessage, sendWithRosterGoAhead } from '../leagueApi';
import { isFinalStatus, leagueStatusLabel, normalizeLeagueStatus } from '../leagueStatus';
import { groupByNight, hasStarted, isRainedOut } from '../leagueGames';
import { eid } from '../../../utils/entityId';
import { AddGameForm, EditGameForm, EnterResultForm } from './LeagueGameForms';
import { eventTimeZone, formatEventClock, wallTimeToDate } from '../../../utils/eventTime';

/**
 * LeagueScheduleTab Component
 *
 * What is still to be played, grouped by night (date header, then each game
 * with time, court and referee team). "Show past nights" lists completed and
 * cancelled games read only. Select / Confirm / Cancel appear only for a
 * manager of a league that is not completed or cancelled, and only on games
 * still waiting for a match.
 *
 * meta { view: 'upcoming' | 'past', total, counts: { to_play, awaiting_result, past, total, locked } }
 * comes from useLeagueDetails; fetchLeagueSchedule({ view, append }) pages it.
 */
/** A game's wall time at the venue, plus the viewer's own time when they are elsewhere. */
const venueGameClock = (match, time, timeZone, formatTime) => {
  const start = timeZone && wallTimeToDate(match.game_date || match.match_date, String(time).slice(0, 5), timeZone);
  return start ? formatEventClock(start, timeZone) : formatTime(time);
};

const LeagueScheduleTab = ({
  league,
  schedule,
  teams,
  canManageLeagues,
  showFeedback,
  openConfirm,
  fetchLeagueSchedule,
  fetchLeagueMatches,
  formatDate,
  formatTime,
  loading,
  onFeedback,
  onLeagueChanged,
  meta = null,
  loadingMore = false,
  navigate,
}) => {
  // Completed and cancelled leagues are read only (LG-B19)
  const isLocked = isFinalStatus(league?.status);
  const showPast = meta?.view === 'past';
  const canAct = canManageLeagues && !isLocked && !showPast;
  const totalGames = meta?.counts?.total ?? schedule.length;
  const viewTotal = meta?.total ?? schedule.length;
  const [showGenerateModal, setShowGenerateModal] = useState(false);
  const [selectedScheduleIds, setSelectedScheduleIds] = useState([]);
  const [scheduleStatusById, setScheduleStatusById] = useState({});
  const [isGeneratingSchedule, setIsGeneratingSchedule] = useState(false);
  const [isClearingSchedule, setIsClearingSchedule] = useState(false);
  const [showAddGame, setShowAddGame] = useState(false);
  // { id, kind: 'edit' | 'result' } — the game whose form is open
  const [openForm, setOpenForm] = useState(null);
  // Games with a match or a result: the schedule can no longer be cleared
  const lockedGames = meta?.counts?.locked ?? 0;
  const scorerTeamIds = meta?.viewer?.scorer_team_ids || [];
  const isRunning = normalizeLeagueStatus(league?.status) === 'in_progress';

  const handleGameSaved = (message) => {
    setShowAddGame(false);
    setOpenForm(null);
    showFeedback('success', message);
    fetchLeagueSchedule();
    fetchLeagueMatches?.();
  };

  const getScheduleId = (match, index) => (
    match.id || `${match.week_number}-${match.team_1_id}-${match.team_2_id}-${match.game_date}-${match.game_time}-${index}`
  );

  const gameClock = (match, time) => venueGameClock(match, time, eventTimeZone(league), formatTime);

  const gameStatus = (match, scheduleId) =>
    scheduleStatusById[scheduleId] || match.effective_status || match.status || 'scheduled';

  // Only a game still waiting for its match can be selected, confirmed or cancelled
  const isActionable = (match, scheduleId) =>
    canAct && gameStatus(match, scheduleId) === 'scheduled' && !match.match_id;

  // An unplayed game can be moved (its match moves with it)
  const canEditGame = (match, scheduleId) =>
    canAct && Boolean(match.id) && gameStatus(match, scheduleId) === 'scheduled';

  // The host or a scorer of either team records a played game's final score
  const canEnterResult = (match, scheduleId) =>
    isRunning && !showPast && Boolean(match.id) &&
    gameStatus(match, scheduleId) === 'scheduled' && hasStarted(match, new Date(), eventTimeZone(league)) &&
    (canManageLeagues || [match.team_1_actual_id, match.team_2_actual_id].some((id) => scorerTeamIds.includes(Number(id))));

  const normalizeScheduleMatch = (match) => ({
    ...match,
    // Link the new match to this exact schedule game (LG-B02)
    league_game_id: match.id,
    team_1_id: match.team_1_actual_id || match.team_1_id,
    team_2_id: match.team_2_actual_id || match.team_2_id,
    game_date: match.game_date || match.match_date,
    game_time: match.game_time || match.match_time
  });

  const handleToggleScheduleSelection = (scheduleId) => {
    setSelectedScheduleIds((prev) => (
      prev.includes(scheduleId) ? prev.filter((id) => id !== scheduleId) : [...prev, scheduleId]
    ));
  };

  const handleSelectAllSchedule = () => {
    const allIds = schedule
      .map((match, index) => getScheduleId(match, index))
      .filter((id, index) => isActionable(schedule[index], id));
    setSelectedScheduleIds(allIds);
  };

  const handleClearScheduleSelection = () => {
    setSelectedScheduleIds([]);
  };

  const handleCreateMatches = async (matchesToCreate) => {
    if (!matchesToCreate.length) {
      showFeedback('warning', 'Select at least one scheduled match to create.');
      return;
    }

    try {
      const response = await sendWithRosterGoAhead((extra) => apiClient.post(`/leagues/${league.id}/bulk-matches`, {
        matches: matchesToCreate,
        schedule_type: 'custom',
        ...extra,
      }));
      if (!response) return;

      const totalCreated = response?.data?.data?.total_created || response?.data?.total_created || 0;
      const skipped = response?.data?.data?.skipped_existing || 0;
      showFeedback(
        'success',
        skipped > 0
          ? `Created ${totalCreated} match(es) from the schedule; ${skipped} already had a match.`
          : `Created ${totalCreated} match(es) from the schedule.`
      );

      // The schedule is kept: games that became matches stay linked to them,
      // and games that were not selected must not be deleted (LG-B02).

      setSelectedScheduleIds([]);
      setScheduleStatusById({});
      // Matches on the calendar start the season
      onLeagueChanged?.({ status: 'in_progress' });
      await fetchLeagueSchedule();
      await fetchLeagueMatches();
    } catch (error) {
      showFeedback('error', `Failed to create matches: ${scheduleErrorMessage(error)}`);
    }
  };

  const handleCreateSelectedMatches = () => {
    const matchesToCreate = schedule
      .map((match, index) => ({
        ...normalizeScheduleMatch(match),
        _scheduleIndex: index
      }))
      .filter((match, index) => selectedScheduleIds.includes(getScheduleId(match, index)));

    if (!matchesToCreate.length) {
      showFeedback('warning', 'Select at least one schedule card to create matches.');
      return;
    }

    openConfirm({
      title: 'Create Matches',
      message: `Create ${matchesToCreate.length} match(es) from the current schedule?`,
      confirmText: 'Create',
      isDanger: false,
      onConfirm: async () => {
        await handleCreateMatches(matchesToCreate);
      }
    });
  };

  const handleConfirmScheduleMatch = (match, index) => {
    const matchToCreate = {
      ...normalizeScheduleMatch(match),
      _scheduleIndex: index
    };
    openConfirm({
      title: 'Create Match',
      message: 'Create this match from the schedule?',
      confirmText: 'Create',
      isDanger: false,
      onConfirm: async () => {
        await handleCreateMatches([matchToCreate]);
      }
    });
  };

  const handleCancelScheduleMatch = (scheduleId) => {
    setScheduleStatusById((prev) => ({ ...prev, [scheduleId]: 'cancelled' }));
    setSelectedScheduleIds((prev) => prev.filter((id) => id !== scheduleId));
    showFeedback('warning', 'Match marked as cancelled in the list.');
  };

  const handleGenerateSchedule = (scheduleType) => {
    const generatorLabel = scheduleType === 'random' ? 'Random' : 'Round Robin';

    openConfirm({
      title: `Generate ${generatorLabel} Schedule`,
      message: totalGames > 0
        ? 'Future games without a match are replaced; games that became matches or are past their date stay. Continue?'
        : `Generate a ${generatorLabel.toLowerCase()} schedule for this league? The season starts once it is saved.`,
      confirmText: 'Generate',
      isDanger: false,
      onConfirm: async () => {
        try {
          setIsGeneratingSchedule(true);
          const res = await sendWithRosterGoAhead((extra) => apiClient.post(`/leagues/${league.id}/generate-schedule`, {
            // 'random' shuffles the draw; both are a full round robin (LG-B29)
            method: scheduleType === 'random' ? 'random' : 'round_robin',
            ...extra,
          }));
          if (!res) return;
          await fetchLeagueSchedule();
          setSelectedScheduleIds([]);
          setScheduleStatusById({});
          onLeagueChanged?.({ status: 'in_progress' });
          showFeedback('success', res.data?.message || `${generatorLabel} schedule generated successfully.`);
        } catch (error) {
          showFeedback('error', `Failed to generate schedule: ${scheduleErrorMessage(error)}`);
        } finally {
          setIsGeneratingSchedule(false);
        }
      }
    });
  };

  const handleClearAllSchedule = () => {
    if (!totalGames) {
      showFeedback('warning', 'No schedule available to clear.');
      return;
    }

    openConfirm({
      title: 'Clear Schedule',
      message: `This will remove all ${totalGames} game${totalGames === 1 ? '' : 's'} of the schedule. Do you want to continue?`,
      confirmText: 'Clear Schedule',
      isDanger: true,
      onConfirm: async () => {
        try {
          setIsClearingSchedule(true);
          await apiClient.delete(`/leagues/${league.id}/clear-schedule`);
          await fetchLeagueSchedule();
          setSelectedScheduleIds([]);
          setScheduleStatusById({});
          showFeedback('success', 'Schedule cleared successfully.');
        } catch (error) {
          const errorMsg = error.response?.data?.message || error.message;
          showFeedback('error', `Failed to clear schedule: ${errorMsg}`);
        } finally {
          setIsClearingSchedule(false);
        }
      }
    });
  };

  const handleScheduleCardClick = (match, scheduleId) => {
    if (!isActionable(match, scheduleId)) return;
    handleToggleScheduleSelection(scheduleId);
  };

  return (
    <div className="schedule-content">
      <div className="content-header">
        <h3>
          League Schedule
          {meta?.counts && (
            <span className="schedule-count">
              {' '}· {meta.counts.to_play} to play
              {meta.counts.awaiting_result > 0 && ` · ${meta.counts.awaiting_result} awaiting a result`}
            </span>
          )}
        </h3>
        {canManageLeagues && !isLocked && (
          <>
          <div className="schedule-actions">
            {totalGames > 0 && (
              <>
                {!showPast && (
                  <>
                <FloatingButton
                  variant="secondary"
                  size="small"
                  onClick={handleSelectAllSchedule}
                  disabled={loading || isGeneratingSchedule || isClearingSchedule}
                >
                  Select All
                </FloatingButton>
                <FloatingButton
                  variant="secondary"
                  size="small"
                  onClick={handleClearScheduleSelection}
                  disabled={loading || isGeneratingSchedule || isClearingSchedule}
                >
                  Clear Selection
                </FloatingButton>
                <FloatingButton
                  variant="primary"
                  size="small"
                  onClick={handleCreateSelectedMatches}
                  disabled={loading || isGeneratingSchedule || isClearingSchedule || selectedScheduleIds.length === 0}
                >
                  Create Matches ({selectedScheduleIds.length})
                </FloatingButton>
                  </>
                )}
                {lockedGames === 0 && (
                <FloatingButton
                  variant="danger"
                  size="small"
                  onClick={handleClearAllSchedule}
                  disabled={loading || isGeneratingSchedule || isClearingSchedule}
                >
                  {isClearingSchedule ? 'Clearing...' : 'Clear Schedule'}
                </FloatingButton>
                )}
              </>
            )}
            <FloatingButton
              variant="secondary"
              size="small"
              onClick={() => setShowAddGame((open) => !open)}
              disabled={loading || isGeneratingSchedule || isClearingSchedule}
            >
              ＋ Add game
            </FloatingButton>
            <FloatingButton
              variant="primary"
              size="small"
              onClick={() => setShowGenerateModal(true)}
              disabled={loading || isGeneratingSchedule || isClearingSchedule}
            >
              📅 Generate Schedule
            </FloatingButton>
            <FloatingButton
              variant="secondary"
              size="small"
              onClick={() => handleGenerateSchedule('round_robin')}
              disabled={loading || isGeneratingSchedule || isClearingSchedule}
              title="Every pairing once over the league's game days, courts and hours"
            >
              {isGeneratingSchedule ? 'Generating…' : '⚙️ Auto Round Robin'}
            </FloatingButton>
          </div>
          <div className="schedule-generator-note">
            <span className="note-icon">ℹ️</span>
            <span>
              <strong>Generate Schedule</strong> — choose dates, pair teams manually or
              auto-generate a round-robin, then confirm matches.
            </span>
          </div>
          </>
        )}
        {canManageLeagues && isLocked && (
          <div style={{ fontSize: '0.82rem', color: 'rgba(239,68,68,0.85)', background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.3)', borderRadius: '0.4rem', padding: '0.35rem 0.75rem' }}>
            🏁 League {leagueStatusLabel(normalizeLeagueStatus(league?.status)).toLowerCase()} — schedule &amp; match creation locked
          </div>
        )}
      </div>

      {totalGames > 0 && (
        <label className="league-past-toggle">
          <input
            type="checkbox"
            checked={showPast}
            onChange={() => {
              setSelectedScheduleIds([]);
              fetchLeagueSchedule({ view: showPast ? 'upcoming' : 'past' });
            }}
          />
          <span>Show past nights{meta?.counts ? ` (${meta.counts.past})` : ''}</span>
        </label>
      )}

      {showAddGame && canManageLeagues && !isLocked && (
        <AddGameForm
          league={league}
          teams={teams}
          onDone={handleGameSaved}
          onCancel={() => setShowAddGame(false)}
        />
      )}

      {totalGames === 0 ? (
        <div className="empty-content">
          <p>No schedule created yet.</p>
          {canManageLeagues && !isLocked && <p>Generate a schedule to see matches here!</p>}
          {canManageLeagues && isLocked && <p>This league is {leagueStatusLabel(league?.status).toLowerCase()}.</p>}
        </div>
      ) : schedule.length === 0 ? (
        <div className="empty-content">
          <p>{showPast ? 'No past nights yet.' : 'Nothing left to play.'}</p>
          {!showPast && <p>Turn on “Show past nights” to see the results.</p>}
        </div>
      ) : (
        <div className="schedule-list">
          {groupByNight(schedule.map((match, index) => ({ match, index })), ({ match }) => match.game_date || match.match_date)
            .map((night) => (
            <section key={night.date} className="schedule-night" aria-label={`Games on ${night.date}`}>
              <h4 className="schedule-night__header">
                📅 {night.date === 'TBD' ? 'Date TBD' : formatDate(night.date)}
                <span className="schedule-night__count">{night.games.length} game{night.games.length !== 1 ? 's' : ''}</span>
              </h4>
              {night.games.map(({ match, index }) => {
            const scheduleId = getScheduleId(match, index);
            const matchTime = match.game_time || match.match_time;
            const team1Id = match.team_1_actual_id || match.team_1_id;
            const team2Id = match.team_2_actual_id || match.team_2_id;
            const team1Name = teams.find((team) => (team.team_id || team.id) === team1Id)?.team_name ||
              teams.find((team) => (team.team_id || team.id) === team1Id)?.name ||
              match.team_1_name || 'TBD';
            const team2Name = teams.find((team) => (team.team_id || team.id) === team2Id)?.team_name ||
              teams.find((team) => (team.team_id || team.id) === team2Id)?.name ||
              match.team_2_name || 'TBD';
            // A team entry shows its logo, a player entry the player's photo
            const entryImage = (teamId) => {
              const entry = teams.find((team) => (team.team_id || team.id) === teamId);
              if (!entry) return null;
              return entry.participant_type === 'user' ? entry.owner_avatar : (entry.logo_url || entry.logo);
            };
            const entryKind = (teamId) =>
              teams.find((team) => (team.team_id || team.id) === teamId)?.participant_type === 'user' ? 'user' : 'team';
            const scheduleStatus = gameStatus(match, scheduleId);
            const actionable = isActionable(match, scheduleId);
            const isSelected = selectedScheduleIds.includes(scheduleId);
            const court = match.court_name || (match.court_number ? `Court ${match.court_number}` : null);
            const statusLabel = scheduleStatus === 'in_progress' ? 'LIVE'
              : scheduleStatus === 'completed' ? `Final ${match.team_1_score ?? 0}–${match.team_2_score ?? 0}`
              : scheduleStatus === 'cancelled' ? (isRainedOut(match) ? 'Rained out' : 'Cancelled')
              : null;
            const matchRef = match.match_short_code || match.match_uuid || match.match_id;
            const editable = canEditGame(match, scheduleId);
            const resultable = canEnterResult(match, scheduleId);
            const formOpen = openForm?.id === match.id ? openForm.kind : null;

            return (
              <div
                key={scheduleId}
                className={`schedule-item schedule-item-${scheduleStatus} ${isSelected ? 'selected' : ''}`}
                onClick={() => handleScheduleCardClick(match, scheduleId)}
                role={actionable ? 'button' : undefined}
                tabIndex={actionable ? 0 : undefined}
                onKeyDown={(event) => {
                  if (actionable && (event.key === 'Enter' || event.key === ' ')) {
                    event.preventDefault();
                    handleScheduleCardClick(match, scheduleId);
                  }
                }}
              >
                {actionable && (
                  <label className="schedule-select">
                    <input
                      type="checkbox"
                      checked={isSelected}
                      onChange={() => handleToggleScheduleSelection(scheduleId)}
                      onClick={(event) => event.stopPropagation()}
                    />
                    <span>Select</span>
                  </label>
                )}
                <div className="match-info">
                  <p className="match-time">⏰ {matchTime ? gameClock(match, matchTime) : 'TBD'}{court ? ` · ${court}` : ''}</p>
                  <Matchup
                    size="sm"
                    className="schedule-item__matchup"
                    home={{ name: team1Name, image: entryImage(team1Id), kind: entryKind(team1Id) }}
                    away={{ name: team2Name, image: entryImage(team2Id), kind: entryKind(team2Id) }}
                  />
                  {match.seeding_game && <span className="league-match-card__tag">Seeding</span>}
                  {match.referee_team_name && <p className="match-ref">🧑‍⚖️ Ref: {match.referee_team_name}</p>}
                  {statusLabel && (
                    <p className={`schedule-item__status schedule-item__status--${scheduleStatus}`}>{statusLabel}</p>
                  )}
                </div>
                {matchRef && navigate && (
                  <button
                    type="button"
                    className="schedule-item__open"
                    onClick={(event) => {
                      event.stopPropagation();
                      navigate(`/matches/${eid({ short_code: match.match_short_code, uuid: match.match_uuid, id: match.match_id })}`, {
                        state: { from: `/leagues/${eid(league) || league?.id}`, tab: 'schedule' },
                      });
                    }}
                  >
                    Open match →
                  </button>
                )}
                {(resultable || editable) && (
                  <div className="schedule-item-actions">
                    {resultable && (
                      <button
                        type="button"
                        className="schedule-item__result"
                        onClick={(event) => {
                          event.stopPropagation();
                          setOpenForm(formOpen === 'result' ? null : { id: match.id, kind: 'result' });
                        }}
                      >
                        Enter result
                      </button>
                    )}
                    {editable && (
                      <button
                        type="button"
                        className="schedule-item__edit"
                        onClick={(event) => {
                          event.stopPropagation();
                          setOpenForm(formOpen === 'edit' ? null : { id: match.id, kind: 'edit' });
                        }}
                      >
                        Edit
                      </button>
                    )}
                  </div>
                )}
                {formOpen === 'result' && (
                  <EnterResultForm
                    league={league}
                    game={match}
                    team1Name={team1Name}
                    team2Name={team2Name}
                    onDone={handleGameSaved}
                    onCancel={() => setOpenForm(null)}
                  />
                )}
                {formOpen === 'edit' && (
                  <EditGameForm league={league} game={match} onDone={handleGameSaved} onCancel={() => setOpenForm(null)} />
                )}
                {actionable && (
                  <div className="schedule-item-actions">
                    <FloatingButton
                      variant="secondary"
                      size="small"
                      onClick={(event) => {
                        event.stopPropagation();
                        handleConfirmScheduleMatch(match, index);
                      }}
                    >
                      Confirm
                    </FloatingButton>
                    <FloatingButton
                      variant="danger"
                      size="small"
                      onClick={(event) => {
                        event.stopPropagation();
                        handleCancelScheduleMatch(scheduleId);
                      }}
                    >
                      Cancel
                    </FloatingButton>
                  </div>
                )}
              </div>
            );
              })}
            </section>
          ))}
        </div>
      )}

      {schedule.length > 0 && schedule.length < viewTotal && (
        <div className="league-load-more">
          <button
            type="button"
            className="league-load-more__btn"
            onClick={() => fetchLeagueSchedule({ append: true })}
            disabled={loadingMore}
          >
            {loadingMore ? 'Loading…' : `Load more (${viewTotal - schedule.length} more)`}
          </button>
        </div>
      )}

      {/* Generate Schedule Modal (unified: auto + manual pairing) */}
      <GenerateScheduleModal
        isOpen={showGenerateModal}
        league={league}
        teams={teams}
        onClose={() => setShowGenerateModal(false)}
        onScheduleCreated={() => {
          setShowGenerateModal(false);
          fetchLeagueSchedule();
          fetchLeagueMatches?.();
        }}
        showFeedback={showFeedback}
        openConfirm={openConfirm}
      />
    </div>
  );
};

export default LeagueScheduleTab;
